import type { Rect } from './camera';
import { obbBounds } from './geometry';
import type { SceneObject, SpatialIndex } from './spatial';

/** Snap distance in screen pixels, whatever the zoom (backlog §1). */
export const SNAP_PX = 6;
const MAX_CANDIDATES = 200;
const EPS = 1e-9;

/**
 * Overlay hints, in world coordinates. `align`: a line at `at` on `axis` ('x' = vertical line) from `from` to `to`;
 * `gap`: equal spacings of `value` (segments along `axis`, drawn at `at` across it); `size`: same width or height.
 */
export type Guide =
  | { kind: 'align'; axis: 'x' | 'y'; at: number; from: number; to: number }
  | { kind: 'gap'; axis: 'x' | 'y'; value: number; segments: { from: number; to: number; at: number }[] }
  | { kind: 'size'; dim: 'w' | 'h'; value: number; rects: Rect[] };

type Axis = 'x' | 'y';
type Span = { lo: number; hi: number; alo: number; ahi: number }; // along the axis, then across it
type Candidate = { d: number; rank: number; guide: (moved: Span) => Guide | null };

const span = (r: Rect, axis: Axis): Span =>
  axis === 'x' ? { lo: r.minX, hi: r.maxX, alo: r.minY, ahi: r.maxY } : { lo: r.minY, hi: r.maxY, alo: r.minX, ahi: r.maxX };
const shift = (s: Span, d: number): Span => ({ ...s, lo: s.lo + d, hi: s.hi + d });
const lines = (s: Span) => [s.lo, (s.lo + s.hi) / 2, s.hi];
const overlapAcross = (a: Span, b: Span) => Math.min(a.ahi, b.ahi) - Math.max(a.alo, b.alo) > 0;
const midAcross = (a: Span, b: Span) => (Math.max(a.alo, b.alo) + Math.min(a.ahi, b.ahi)) / 2;

function alignCandidates(m: Span, ns: Span[], axis: Axis, thr: number): Candidate[] {
  const out: Candidate[] = [];
  for (const n of ns)
    for (const at of lines(n))
      for (const own of lines(m)) {
        const d = at - own;
        if (Math.abs(d) > thr) continue;
        out.push({
          d,
          rank: 0,
          guide: (moved) => ({ kind: 'align', axis, at, from: Math.min(n.alo, moved.alo), to: Math.max(n.ahi, moved.ahi) }),
        });
      }
  return out;
}

/** Equal spacing along `axis` with the neighbors in the same row (overlapping across it). */
function gapCandidates(m: Span, ns: Span[], axis: Axis, thr: number): Candidate[] {
  const row = ns.filter((n) => overlapAcross(n, m)).sort((a, b) => a.lo - b.lo || a.hi - b.hi);
  const pairs: [Span, Span][] = [];
  for (let i = 1; i < row.length; i++) if (row[i]!.lo >= row[i - 1]!.hi) pairs.push([row[i - 1]!, row[i]!]);
  const left = row.filter((n) => n.hi <= m.lo).sort((a, b) => b.hi - a.hi)[0];
  const right = row.filter((n) => n.lo >= m.hi).sort((a, b) => a.lo - b.lo)[0];
  const out: Candidate[] = [];
  const add = (d: number, value: number, segs: (moved: Span) => [Span, Span][]) => {
    if (Math.abs(d) > thr || value < 0) return;
    out.push({
      d,
      rank: 1,
      guide: (moved) => ({
        kind: 'gap',
        axis,
        value,
        segments: segs(moved)
          .map(([a, b]) => ({ from: a.hi, to: b.lo, at: midAcross(a, b) }))
          .sort((p, q) => p.from - q.from || p.at - q.at),
      }),
    });
  };
  for (const [a, b] of pairs) {
    const g = b.lo - a.hi;
    const same = pairs.filter(([p, q]) => Math.abs(q.lo - p.hi - g) < EPS);
    if (left) add(left.hi + g - m.lo, g, (moved) => [...same, [left, moved]]);
    if (right) add(right.lo - g - m.hi, g, (moved) => [...same, [moved, right]]);
  }
  if (left && right) {
    const g = (right.lo - left.hi - (m.hi - m.lo)) / 2;
    add(left.hi + g - m.lo, g, (moved) => [[left, moved], [moved, right]]);
  }
  return out;
}

/**
 * The grid line for a dragged edge at `pos` whose opposite edge is at `fixed`: the nearest one, or the next one away
 * from `fixed` when the nearest would leave less than half a cell (a small box never collapses onto its other edge).
 */
function gridEdge(pos: number, fixed: number, edge: 'min' | 'max', grid: number): number {
  const t = snapToGrid(pos, grid);
  if (edge === 'max') return t - fixed < grid / 2 ? t + grid : t;
  return fixed - t < grid / 2 ? t - grid : t;
}

/** The grid line nearest to `v` (never -0). */
export function snapToGrid(v: number, grid: number): number {
  return Math.round(v / grid) * grid || 0;
}

/** A box with both corners on the grid, never under one cell on a side (box creation, P16). */
export function snapBoxToGrid(b: { x: number; y: number; w: number; h: number }, grid: number): { x: number; y: number; w: number; h: number } {
  const x = snapToGrid(b.x, grid);
  const y = snapToGrid(b.y, grid);
  return { x, y, w: Math.max(grid, snapToGrid(b.x + b.w, grid) - x), h: Math.max(grid, snapToGrid(b.y + b.h, grid) - y) };
}

function solveAxis(m: Span, ns: Span[], axis: Axis, thr: number, grid: number | null): { d: number; guides: Guide[] } {
  const cands = [...alignCandidates(m, ns, axis, thr), ...gapCandidates(m, ns, axis, thr)];
  // With the grid on, the grid always wins (P16, backlog §19); guides stay only where they agree with it.
  const onGrid = grid && grid > 0 ? { d: snapToGrid(m.lo, grid) - m.lo, rank: 2, guide: () => null } : null;
  if (onGrid) cands.push(onGrid);
  if (cands.length === 0) return { d: 0, guides: [] };
  const best = onGrid ?? cands.reduce((a, b) => {
    const da = Math.abs(a.d);
    const db = Math.abs(b.d);
    return db < da - EPS || (Math.abs(db - da) <= EPS && (b.rank < a.rank || (b.rank === a.rank && b.d < a.d))) ? b : a;
  });
  const moved = shift(m, best.d);
  const seen = new Set<string>();
  const guides: Guide[] = [];
  for (const c of cands) {
    if (Math.abs(c.d - best.d) > EPS) continue;
    const g = c.guide(moved);
    const key = g && JSON.stringify(g);
    if (g && key && !seen.has(key)) {
      seen.add(key);
      guides.push(g);
    }
  }
  guides.sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1));
  return { d: Number.isFinite(best.d) ? best.d : 0, guides: guides.filter((g) => !JSON.stringify(g).includes('null')) };
}

/**
 * Correction for a box being moved: edges and centres on the neighbors', equal spacing in a row or column, optionally
 * the grid. The threshold is in screen pixels (`threshold / zoom` in the world); on each axis the smallest correction
 * wins. Pure: the result never depends on the neighbors' order.
 */
export function snapMove(
  moving: Rect,
  neighbors: readonly Rect[],
  zoom: number,
  opts: { grid?: number | null; threshold?: number } = {},
): { dx: number; dy: number; guides: Guide[] } {
  const thr = (opts.threshold ?? SNAP_PX) / zoom;
  const grid = opts.grid ?? null;
  const x = solveAxis(span(moving, 'x'), neighbors.map((n) => span(n, 'x')), 'x', thr, grid);
  const y = solveAxis(span(moving, 'y'), neighbors.map((n) => span(n, 'y')), 'y', thr, grid);
  // Guides were computed per axis: re-anchor the across extents on the fully moved box.
  const moved: Rect = { minX: moving.minX + x.d, maxX: moving.maxX + x.d, minY: moving.minY + y.d, maxY: moving.maxY + y.d };
  const fix = (g: Guide): Guide => {
    if (g.kind !== 'align') return g;
    const across = g.axis === 'x' ? [moved.minY, moved.maxY] : [moved.minX, moved.maxX];
    const n = neighbors.map((r) => span(r, g.axis)).filter((s) => lines(s).some((l) => Math.abs(l - g.at) < EPS));
    const lo = Math.min(across[0]!, ...n.map((s) => s.alo));
    const hi = Math.max(across[1]!, ...n.map((s) => s.ahi));
    return { ...g, from: lo, to: hi };
  };
  return { dx: x.d, dy: y.d, guides: [...x.guides, ...y.guides].map(fix).sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1)) };
}

/**
 * Correction for a box being resized by the given edges: the moving edge on a neighbor's edge or centre, or the size
 * on a neighbor's width/height, or on the grid when `grid` is set; the opposite edge never moves.
 */
export function snapResize(
  box: Rect,
  edges: { x: 'min' | 'max' | null; y: 'min' | 'max' | null },
  neighbors: readonly Rect[],
  zoom: number,
  opts: { threshold?: number; grid?: number | null } = {},
): { box: Rect; guides: Guide[] } {
  const thr = (opts.threshold ?? SNAP_PX) / zoom;
  const out = { ...box };
  const guides: Guide[] = [];
  for (const axis of ['x', 'y'] as const) {
    const edge = edges[axis];
    if (!edge) continue;
    const s = span(box, axis);
    const pos = edge === 'min' ? s.lo : s.hi;
    const size = s.hi - s.lo;
    type C = { d: number; guide: Guide };
    const cands: C[] = [];
    for (const n of neighbors) {
      const ns = span(n, axis);
      for (const at of lines(ns)) {
        const d = at - pos;
        if (Math.abs(d) <= thr) cands.push({ d, guide: { kind: 'align', axis, at, from: Math.min(ns.alo, s.alo), to: Math.max(ns.ahi, s.ahi) } });
      }
      const nsize = ns.hi - ns.lo;
      const d = (nsize - size) * (edge === 'max' ? 1 : -1);
      if (Math.abs(d) <= thr) cands.push({ d, guide: { kind: 'size', dim: axis === 'x' ? 'w' : 'h', value: nsize, rects: [n] } });
    }
    // The grid wins when on (P16): the dragged edge goes to its nearest line, guides only where they agree.
    const grid = opts.grid && opts.grid > 0 ? { d: gridEdge(pos, edge === 'max' ? s.lo : s.hi, edge, opts.grid) - pos } : null;
    if (!cands.length && !grid) continue;
    const best = grid ?? cands.reduce((a, b) => (Math.abs(b.d) < Math.abs(a.d) - EPS || (Math.abs(Math.abs(b.d) - Math.abs(a.d)) <= EPS && b.d < a.d) ? b : a));
    if (axis === 'x') out[edge === 'min' ? 'minX' : 'maxX'] += best.d;
    else out[edge === 'min' ? 'minY' : 'maxY'] += best.d;
    for (const c of cands) if (Math.abs(c.d - best.d) <= EPS) guides.push(c.guide.kind === 'size' ? { ...c.guide, rects: [...c.guide.rects, out] } : c.guide);
  }
  return { box: out, guides };
}

/** Bounds of the objects worth snapping to: in `area` (the viewport), not excluded, the closest to its centre first. */
export function snapCandidates(index: SpatialIndex, objs: ReadonlyMap<string, SceneObject>, area: Rect, exclude: ReadonlySet<string>): Rect[] {
  const cx = (area.minX + area.maxX) / 2;
  const cy = (area.minY + area.maxY) / 2;
  return index
    .queryRect(area)
    .filter((id) => !exclude.has(id))
    .map((id) => objs.get(id))
    .filter((o): o is SceneObject => !!o && o.type !== 'connector')
    .map((o) => obbBounds(o.obb))
    .sort((a, b) => Math.hypot((a.minX + a.maxX) / 2 - cx, (a.minY + a.maxY) / 2 - cy) - Math.hypot((b.minX + b.maxX) / 2 - cx, (b.minY + b.maxY) / 2 - cy))
    .slice(0, MAX_CANDIDATES);
}
